import React, { useContext, useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { QmsDataEditModalContext } from "../_common/QmsDataTable";
import cmtStageActions from "../../../_actions/_qmsActions/cmtStageActions";
import qmsTeamActions from "../../../_actions/_qmsActions/qmsTeamActions";
import qmsWorkflowActions from "../../../_actions/_qmsActions/qmsWorkflowActions";
import { QMS } from "../../../_constants/actionConstants";
import { useRef } from "react";

export default function QmsAdminModal_EditWorkflowNode(props) {
  const { divisionId, modalState, toggleModal } = props;

  // Below ref is used to prevent validation errors from showing when
  // form is first loaded.
  const isBeforeInitialFormValidation = useRef(true);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(cmtStageActions.getAll(divisionId));
    dispatch(qmsTeamActions.getAll(divisionId));
    dispatch(qmsWorkflowActions.getRelationshipTypes());
  }, []);

  const cmtStages = useSelector((state) => state.qms.cmtStages[divisionId] || null);
  const cmtStageOptions = useMemo(
    () => (cmtStages || []).map((cs) => ({ value: cs.cmtStageId, label: cs.title })),
    [cmtStages]
  );

  const qmsTeams = useSelector((state) => state.qms.qmsTeams[divisionId] || null);
  const qmsTeamOptions = useMemo(
    () => (qmsTeams || []).map((qt) => ({ value: qt.qmsTeamId, label: qt.name })),
    [qmsTeams]
  );

  const relationshipTypes = useSelector((state) => state.qms.relationshipTypes || null);
  const relationshipTypeOptions = useMemo(
    () =>
      (relationshipTypes || []).map((rt) => ({ value: rt.value, label: rt.displayName })),
    [relationshipTypes]
  );

  const [selectedCmtStage, selectCmtStage] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedOwnerTeam, selectOwnerTeam] = useState(null);
  const [duration, setDuration] = useState("0");
  const [selectedRelationshipType, selectRelationshipType] = useState(null);
  const [formErrors, setFormErrors] = useState({
    title: null,
  });

  useEffect(() => {
    if (isBeforeInitialFormValidation.current)
      isBeforeInitialFormValidation.current = false;
    else validateForm("ownerTeam");
  }, [selectedOwnerTeam]);

  useEffect(() => {
    if (modalState.data) {
      cmtStageOptions &&
        modalState.data.cmtStageId &&
        selectCmtStage(
          cmtStageOptions.find((o) => o.value === modalState.data.cmtStageId) || null
        );

      qmsTeamOptions &&
        modalState.data.ownerTeamId &&
        selectOwnerTeam(
          qmsTeamOptions.find((o) => o.value === modalState.data.ownerTeamId) || null
        );

      relationshipTypeOptions &&
        modalState.data.relationshipTypeValue &&
        selectRelationshipType(
          relationshipTypeOptions.find(
            (o) => o.value === modalState.data.relationshipTypeValue
          ) || null
        );

      setTitle(modalState.data.title || "");
      setDescription(modalState.data.description || "");
      setDuration(modalState.data.duration || "");
    }
  }, [cmtStageOptions, qmsTeamOptions, relationshipTypeOptions]);

  useEffect(() => {
    if (selectedCmtStage) {
      const cmtStage = (cmtStages || []).find(
        (s) => s.cmtStageId === selectedCmtStage.value
      );
      if (!cmtStage) return;
      const ownerTeam = cmtStage.ownerTeamId
        ? (qmsTeams || []).find((t) => t.qmsTeamId === cmtStage.ownerTeamId)
        : null;
      setTitle(cmtStage.title || "");
      setDescription(cmtStage.description || "");
      setDuration(cmtStage.setDuration || "");
      selectOwnerTeam(
        ownerTeam
          ? (qmsTeamOptions || []).find((o) => o.value === ownerTeam.qmsTeamId)
          : null
      );
    }
  }, [selectedCmtStage]);

  const validateForm = (fieldName) => {
    const defaultMsg = "Required";
    switch (fieldName) {
      case "title":
        setFormErrors({ ...formErrors, title: !title ? defaultMsg : null });
        break;
      default:
        break;
    }
  };

  const formIsIncomplete = () => Object.values(formErrors).some((e) => e) || !title;

  const handleClick_submit = async (e) => {
    e.preventDefault();
    console.log("modalState.data=", modalState.data);
    const nodeData = {
      id: modalState.data ? modalState.data.id : 0,
      label: title,
      title,
      description,
      ownerTeamName: selectedOwnerTeam ? selectedOwnerTeam.label : null,
      ownerTeamId: selectedOwnerTeam ? selectedOwnerTeam.value : null,
      duration,
      relationshipTypeValue: selectedRelationshipType
        ? selectedRelationshipType.value
        : null,
      relationshipTypeDisplayName: selectedRelationshipType
        ? selectedRelationshipType.name
        : null,
    };
    modalState.submitCallback(nodeData);
    toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <Modal isOpen={modalState.showModal} toggle={toggleModal} fade={false} size={"lg"}>
      <ModalHeader toggle={toggleModal} className="bg-info text-white" close={closeBtn}>
        <FontAwesomeIcon
          icon={modalState.data ? "edit" : "plus"}
          className="fa-fw mr-2"
        />
        {modalState.data ? "Update Node" : "Add Node"}
      </ModalHeader>
      <ModalBody>
        <div className="px-3">
          <div className="row">
            <div className="form-group col-12 mb-1">
              <label htmlFor="cmtStage" className={`mb-0`}>
                Preset Stage:
              </label>
              <Select
                name="cmtStage"
                id="cmtStage"
                value={selectedCmtStage}
                options={cmtStageOptions}
                onChange={(selected) => selectCmtStage(selected)}
                isClearable
              />
            </div>
          </div>
          <div className="row">
            <div className="form-group col-12 mb-1">
              <label
                htmlFor="title"
                className={`mb-0${selectedCmtStage ? "" : " font-weight-bold"}`}
              >
                Title:{selectedCmtStage ? "" : "*"}
              </label>
              <input
                id="title"
                name="title"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={selectedCmtStage}
                onBlur={(e) => validateForm(e.target.name)}
              />
              {formErrors.title && (
                <span className="error-small">
                  <small>{formErrors.title}</small>
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-group col-12 mb-1">
              <label htmlFor="ownerTeam" className="mb-0">
                Stage Owner:
              </label>
              <Select
                id="ownerTeam"
                name="ownerTeam"
                className="pl-0"
                value={selectedOwnerTeam}
                options={qmsTeamOptions}
                onChange={(selected) => selectOwnerTeam(selected)}
                onBlur={() => validateForm("ownerTeam")}
                isClearable
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-group col-12 mb-1">
              <label htmlFor="description" className="mb-0">
                Node Description:
              </label>
              <textarea
                id="description"
                name="description"
                className="form-control"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-group col-sm-4 mb-1 pr-1">
              <label htmlFor="duration" className="mb-0">
                Expected Duration:
              </label>
              <div className="input-group mb-0">
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  className="form-control"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                />
                <div className="input-group-append">
                  <span className="input-group-text">Days</span>
                </div>
              </div>
            </div>
            <div className="form-group col-sm-8 mb-1 pl-1">
              <label htmlFor="relationshipType" className="mb-0">
                Relationship Type:
              </label>
              <Select
                id="relationshipType"
                name="relationshipType"
                options={relationshipTypeOptions}
                value={selectedRelationshipType}
                onChange={(selected) => selectRelationshipType(selected)}
                isClearable
              />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bg-lightgray">
        <div className="form-row form-group mb-0">
          <button
            className="btn btn-primary"
            name="addNode"
            onClick={handleClick_submit}
            disabled={formIsIncomplete()}
          >
            <b>{modalState.data ? "Update Node" : "Add Node"}</b>
          </button>
          <button
            className="btn btn-secondary ml-2"
            color="secondary"
            onClick={toggleModal}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
